import { Link } from "wouter";
import {
  Download,
  Monitor,
  ScanLine,
  Printer,
  ShoppingCart,
  RefreshCw,
  WifiOff,
  ShieldCheck,
  CheckCircle2,
  HardDrive,
  Cpu,
  ArrowRight,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";

// ── Desktop downloads ────────────────────────────────────────────────────────
// Data-driven so new versions / additional files (32-bit, portable, older builds)
// are a one-line add later. `href` is a placeholder "#" for now — replace each with
// the real Bunny CDN .exe link once uploaded (the CDN should send Content-Disposition
// so the browser downloads rather than navigates).
type Release = {
  name: string;
  platform: string;
  version: string;
  size?: string;
  href: string;
  featured?: boolean;
};

const RELEASES: Release[] = [
  {
    name: "DailyHisaab Desktop",
    platform: "Windows 10 & 11 · 64-bit",
    version: "v1.0.2",
    size: undefined,
    href: "https://dailyhisaab-downloads.b-cdn.net/DailyHisaab%20Setup%201.0.2.zip",
    featured: true,
  },
];

const FEATURES = [
  { icon: ShoppingCart, title: "Full POS billing", desc: "Fast counter billing with keyboard shortcuts, built for a real shop desk." },
  { icon: ScanLine, title: "Barcode scanning", desc: "Plug in a USB/handheld scanner and ring up items instantly." },
  { icon: Printer, title: "Thermal printing", desc: "Print receipts and barcode labels on Bluetooth, USB, or network printers." },
  { icon: RefreshCw, title: "Auto-sync", desc: "Sales, stock, and reports sync with your phone within seconds." },
  { icon: WifiOff, title: "Works offline", desc: "Keep billing during outages — everything syncs when you're back online." },
  { icon: ShieldCheck, title: "Same secure account", desc: "Sign in with your existing DailyHisaab account. Nothing new to set up." },
];

const STEPS = [
  { n: 1, title: "Download the installer", desc: "Click the button above to get the Windows setup file." },
  { n: 2, title: "Run the setup", desc: "Open the downloaded file and follow the on-screen steps to install." },
  { n: 3, title: "Sign in", desc: "Log in with your DailyHisaab account — your devices sync within seconds." },
];

export default function Downloads() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link href="/">
            <Logo size="md" />
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <main className="flex-1 pb-20">
        {/* ── HERO ── */}
        <section className="container mx-auto px-4 pt-14 pb-10 max-w-3xl text-center">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-3xl bg-primary/10 border border-primary/15 mb-6">
            <Monitor className="h-10 w-10 text-primary" />
          </div>
          <p className="text-xs font-bold tracking-[0.2em] text-primary uppercase mb-3">
            DailyHisaab for Windows
          </p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-foreground">
            Download DailyHisaab Desktop
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Run full POS billing, barcode scanning, and thermal printing on your Windows PC.
            Keep your phone for reports, analysis, and staff admin — everything stays in sync.
          </p>
        </section>

        {/* ── DOWNLOAD CARDS ── */}
        <section className="container mx-auto px-4 max-w-3xl space-y-4">
          {RELEASES.map((r) => (
            <div
              key={r.name + r.version}
              className={`bg-card rounded-3xl p-6 md:p-8 border shadow-sm ${
                r.featured ? "border-primary/30 shadow-lg" : "border-border"
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 shrink-0">
                  <Monitor className="h-7 w-7 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-bold text-foreground">{r.name}</h2>
                    {r.featured && (
                      <span className="text-[10px] font-black tracking-wide uppercase text-primary bg-primary/10 rounded-full px-2 py-0.5">
                        {r.version}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {r.platform}
                    {r.size ? ` · ${r.size}` : ""}
                  </p>
                </div>
                <a
                  href={r.href}
                  className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-3.5 px-7 rounded-xl shadow-md transition-transform active:scale-95 whitespace-nowrap"
                  data-testid="button-download-desktop"
                >
                  <Download className="h-5 w-5" />
                  <span>Download for Windows</span>
                </a>
              </div>
            </div>
          ))}
          <p className="text-center text-sm text-muted-foreground pt-1">
            The download is a <span className="font-medium text-foreground">.zip</span> — unzip it and run
            the setup file inside. More versions and download options coming soon.
          </p>
        </section>

        {/* ── FEATURES ── */}
        <section className="container mx-auto px-4 max-w-4xl mt-14">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            What runs on DailyHisaab Desktop
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-card rounded-2xl p-6 border border-border shadow-sm">
                <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-primary/10 mb-4">
                  <f.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-bold text-foreground">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── HOW TO INSTALL ── */}
        <section className="container mx-auto px-4 max-w-3xl mt-14">
          <div className="bg-card rounded-3xl p-8 border border-border shadow-sm">
            <h2 className="text-2xl font-bold text-foreground mb-6">How to install</h2>
            <div className="space-y-4">
              {STEPS.map((s) => (
                <div key={s.n} className="flex items-start gap-4">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary font-bold text-sm shrink-0">
                    {s.n}
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">{s.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SYSTEM REQUIREMENTS ── */}
        <section className="container mx-auto px-4 max-w-3xl mt-6">
          <div className="bg-card rounded-3xl p-8 border border-border shadow-sm">
            <h2 className="text-2xl font-bold text-foreground mb-6">System requirements</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-3">
                <Monitor className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span><strong className="text-foreground">Windows 10 or 11</strong> (64-bit)</span>
              </li>
              <li className="flex items-start gap-3">
                <Cpu className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span>4 GB RAM or more recommended</span>
              </li>
              <li className="flex items-start gap-3">
                <HardDrive className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span>~250 MB free disk space</span>
              </li>
              <li className="flex items-start gap-3">
                <RefreshCw className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span>Internet connection for account sign-in and sync</span>
              </li>
            </ul>
          </div>
        </section>

        {/* ── ULTIMATE NOTE ── */}
        <section className="container mx-auto px-4 max-w-3xl mt-6">
          <div className="flex flex-col sm:flex-row items-start gap-4 bg-primary/5 border border-primary/15 rounded-3xl p-6">
            <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-foreground font-semibold">Already using DailyHisaab on your phone?</p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                Just install Desktop and sign in with the same account — your business, products, and
                sales appear automatically. Full Desktop POS is part of the Ultimate plan.
              </p>
              <Link
                href="/"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-3 hover:underline"
              >
                Manage your plan <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ── FOOTER ── */}
      <footer className="border-t border-border py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <Logo size="sm" />
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <Link href="/privacy" className="hover:text-primary transition-colors whitespace-nowrap">
                Privacy Policy
              </Link>
              <Link href="/terms" className="hover:text-primary transition-colors whitespace-nowrap">
                Terms of Service
              </Link>
              <Link href="/shipping-refunds" className="hover:text-primary transition-colors whitespace-nowrap">
                Refund Policy
              </Link>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Made with care in Bengal by{" "}
                <a
                  href="https://asldigitalsolutions.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold hover:underline"
                >
                  ASL Digital Solutions
                </a>
              </p>
              <p className="text-sm text-muted-foreground">&copy;2026 DailyHisaab. All Rights Reserved</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
